import React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useMessaging } from '../contexts/MessagingProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import type { User, Project } from '../types';
import BriefcaseIcon from './icons/BriefcaseIcon';
import MapPinIcon from './icons/MapPinIcon';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import EditIcon from './icons/EditIcon';
import DocumentIcon from './icons/DocumentIcon';
import HeartIcon from './icons/HeartIcon';
import StarIcon from './icons/StarIcon';
import ChatBubbleIcon from './icons/ChatBubbleIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import YouTubeIcon from './icons/YouTubeIcon';
import LinkIcon from './icons/LinkIcon';
import { openAttachment } from '../utils/fileUtils';

/**
 * Main Profile View router. Determines own profile vs public profile.
 */
const ProfilePage: React.FC = () => {
    const { currentUser } = useAuth();
    const { viewingProfile, handleNavigation, navigationOrigin } = useNavigation();
    const { t } = useLanguage();

    if (!viewingProfile) return null;

    const isOwnProfile = currentUser?.handle === viewingProfile.handle;

    return (
        <div className="space-y-6 font-urbanist">
            <div className="flex justify-between items-center">
                <button onClick={() => handleNavigation(navigationOrigin || 'feed')} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>{t('BACK')}</span>
                </button>
                {isOwnProfile && (
                    <button 
                        onClick={() => handleNavigation('editProfile')}
                        className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded-full text-xs tracking-widest transition-all shadow-lg"
                    >
                        <EditIcon className="h-4 w-4" />
                        <span>{t('EDIT_PROFILE')}</span>
                    </button>
                )}
            </div>
            <ProfileView user={viewingProfile} isOwnProfile={isOwnProfile} />
        </div>
    );
};

/**
 * Reusable profile component displaying user identity and data cards.
 */
const ProfileView: React.FC<{ user: User; isOwnProfile: boolean; }> = ({ user, isOwnProfile }) => {
  const { currentUser, handleToggleUserFan, handleEndorseUser, handleUnendorseUser, users: allUsers } = useAuth();
  const { handleViewBand, handleViewFanList, handleViewEndorserList, bands, handleNavigation, handleViewProfile } = useNavigation();
  const { setActiveConversationPartner } = useMessaging();
  const { t } = useLanguage();
  
  const fansCount = user.fans?.length || 0;
  const endorsementsCount = user.endorsedBy?.length || 0;
  const isFanOfThisUser = currentUser && user.fans?.includes(currentUser.handle);
  const isEndorsedByMe = currentUser && currentUser.endorsements?.includes(user.handle);
  
  const isFanProfile = user.subscriptionTier === 'fan_basic';

  // Clusters Identification
  const PRO_CLUSTERS = ['talent', 'business', 'service', 'edu', 'multi'];
  
  const getCluster = (tier?: string) => {
      if (!tier) return 'fan';
      if (tier === 'fan_basic') return 'fan';
      return PRO_CLUSTERS.find(c => tier.includes(c)) || 'fan';
  };

  const viewerCluster = getCluster(currentUser?.subscriptionTier);
  const targetCluster = getCluster(user.subscriptionTier);

  const isViewerPro = PRO_CLUSTERS.includes(viewerCluster);
  const isTargetPro = PRO_CLUSTERS.includes(targetCluster);
  const isViewerFan = viewerCluster === 'fan';

  const handleMessage = () => {
      setActiveConversationPartner(user);
      handleNavigation('messages');
  };

  const handleProjectClick = (project: Project) => {
    const cleanName = project.name.toLowerCase().trim().replace(/[^a-z0-9_]/g, '-');
    const cleanCity = project.city.toLowerCase().trim().replace(/[^a-z0-9_]/g, '-');
    const bandId = `${cleanName}-${cleanCity}`;
    const fullBand = bands.find(b => b.id === bandId);
    if (fullBand) handleViewBand(fullBand);
  };

  const displayRoles = user.roles && user.roles.length > 0 
    ? user.roles.join(' / ') 
    : user.role || 'Member';

  // Endorsements list for local display
  const endorsementsList = React.useMemo(() => {
    return (user.endorsedBy || [])
        .map(h => allUsers.find(u => u.handle === h))
        .filter(Boolean) as User[];
  }, [user.endorsedBy, allUsers]);

  const hasSocials = user.socialLinks?.facebook || user.socialLinks?.instagram || user.socialLinks?.youtube;
  const hasMediaLinks = user.embeddedMedia && user.embeddedMedia.length > 0;

  return (
    <div className="space-y-6 font-urbanist">
       <div className="bg-brand-surface rounded-lg overflow-hidden border border-gray-800 shadow-sm relative">
            <div className="h-48 md:h-64 bg-gray-800 bg-cover bg-center" style={{ backgroundImage: user.bannerUrl ? `url(${user.bannerUrl})` : 'none' }}>
                {!isOwnProfile && (
                    <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
                        {/* Message Button */}
                        <button 
                            onClick={handleMessage}
                            className="flex items-center justify-center space-x-2 bg-brand-dark/60 backdrop-blur-md text-white border border-white/20 hover:bg-brand-orange hover:text-white px-5 py-2.5 rounded-full font-bold tracking-widest text-[10px] transition-all shadow-xl uppercase"
                        >
                            <ChatBubbleIcon className="h-3.5 w-3.5" />
                            <span>{t('MESSAGE')}</span>
                        </button>

                        {/* Pro-to-Pro Endorsement */}
                        {isViewerPro && isTargetPro && (
                            <button 
                                onClick={() => isEndorsedByMe ? handleUnendorseUser(user.handle) : handleEndorseUser(user.handle)}
                                className={`flex items-center justify-center space-x-2 px-5 py-2.5 rounded-full font-bold tracking-widest text-[10px] transition-all shadow-xl uppercase border ${
                                    isEndorsedByMe 
                                    ? 'bg-brand-orange text-white border-brand-orange' 
                                    : 'bg-brand-dark/60 backdrop-blur-md text-white border-white/20 hover:bg-brand-orange hover:text-white'
                                }`}
                            >
                                <StarIcon className={`h-3.5 w-3.5 ${isEndorsedByMe ? 'fill-current' : ''}`} />
                                <span>{isEndorsedByMe ? t('ENDORSED') : t('ENDORSE')}</span>
                            </button>
                        )}

                        {/* Fan Support: Only visible to Fan cluster */}
                        {isViewerFan && (
                            <button 
                                onClick={() => handleToggleUserFan(user.handle)}
                                className={`flex items-center justify-center space-x-2 px-5 py-2.5 rounded-full font-bold tracking-widest text-[10px] transition-all shadow-xl uppercase border ${
                                    isFanOfThisUser 
                                    ? 'bg-pink-600 text-white border-pink-600 shadow-pink-600/20' 
                                    : 'bg-brand-dark/60 backdrop-blur-md text-white border-white/20 hover:bg-pink-500 hover:text-white'
                                }`}
                            >
                                <HeartIcon className={`h-3.5 w-3.5 ${isFanOfThisUser ? 'fill-current' : ''}`} />
                                <span>{isFanOfThisUser ? "I'm a Fan" : "Become a Fan"}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="px-6 pb-6 relative">
                <div className="flex flex-col md:flex-row md:items-end -mt-16 md:-mt-20 space-y-4 md:space-y-0 md:space-x-6">
                    <img src={user.avatarUrl} alt={user.name} className="h-32 w-32 md:h-40 md:w-40 rounded-full border-4 border-brand-surface shadow-xl bg-gray-700 object-cover" />
                    <div className="flex-1 min-w-0">
                        <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-none uppercase break-words pr-2 md:pr-10">{user.name}</h1>
                        {!isFanProfile && <p className="text-xl text-brand-orange mt-1 font-bold truncate">{user.title}</p>}
                        <div className="flex items-center text-brand-muted mt-2 space-x-4">
                            <div className="flex items-center"><MapPinIcon className="h-4 w-4 mr-1 text-brand-orange" /><span>{user.city}</span></div>
                            {!isFanProfile && <span className="bg-gray-800 text-gray-300 text-[10px] font-bold px-2 py-0.5 rounded tracking-widest uppercase">{displayRoles}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
              <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">{t('ABOUT')}</h2>
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed font-medium">{user.collaborations || 'No description provided.'}</p>
          </div>

          {!isFanProfile && (
              <>
                {(user.rider || (user.riderFiles && user.riderFiles.length > 0)) && (
                    <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                        <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">{t('RIDER_SPECS')}</h2>
                        {user.rider && <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed mb-4">{user.rider}</p>}
                        {user.riderFiles && user.riderFiles.length > 0 && (
                            <ul className="space-y-2">
                                {user.riderFiles.map(file => (
                                    <li key={file.id}>
                                        <button 
                                            onClick={() => openAttachment(file)}
                                            className="flex items-center space-x-2 text-sm text-brand-orange hover:underline text-left w-full focus:outline-none"
                                        >
                                            <DocumentIcon className="h-4 w-4" />
                                            <span className="truncate font-bold">{file.name}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {user.photos && user.photos.length > 0 && (
                    <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                        <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">{t('GALLERY_SHOWCASE')}</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {user.photos.map(photo => (
                                <div key={photo.id} className="aspect-square rounded-lg overflow-hidden border border-gray-700">
                                    <img src={photo.url} className="w-full h-full object-cover" alt="Showcase" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <ProjectsCard projects={user.projects || []} onProjectClick={handleProjectClick} />
              </>
          )}
        </div>

        <div className="space-y-6">
            {!isFanProfile && (
                <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Skills</h2>
                    <div className="flex flex-wrap gap-2">
                        {user.instruments?.map(i => <span key={i} className="bg-gray-800 text-brand-orange px-3 py-1 rounded-full text-[10px] font-extrabold border border-brand-orange/20 tracking-widest uppercase">{i}</span>)}
                        {user.genres?.map(g => <span key={g} className="bg-gray-800 text-blue-400 px-3 py-1 rounded-full text-[10px] font-extrabold border border-blue-400/20 tracking-widest uppercase">{g}</span>)}
                    </div>
                </div>
            )}

            {hasSocials && (
                <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Social</h2>
                    <div className="grid grid-cols-3 gap-4">
                        {user.socialLinks?.facebook && (
                            <a href={user.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-blue-600/10 rounded-xl border border-blue-600/20 hover:bg-blue-600/20 transition-all group">
                                <FacebookIcon className="h-6 w-6 text-blue-600 group-hover:scale-110 transition-transform" />
                            </a>
                        )}
                        {user.socialLinks?.instagram && (
                            <a href={user.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-pink-600/10 rounded-xl border border-pink-600/20 hover:bg-pink-600/20 transition-all group">
                                <InstagramIcon className="h-6 w-6 text-pink-600 group-hover:scale-110 transition-transform" />
                            </a>
                        )}
                        {user.socialLinks?.youtube && (
                            <a href={user.socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-3 bg-red-600/10 rounded-xl border border-red-600/20 hover:bg-red-600/20 transition-all group">
                                <YouTubeIcon className="h-6 w-6 text-red-600 group-hover:scale-110 transition-transform" />
                            </a>
                        )}
                    </div>
                </div>
            )}

            {hasMediaLinks && (
                <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                    <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">Media</h2>
                    <div className="space-y-2">
                        {user.embeddedMedia?.map((item) => (
                            <a 
                                key={item.id} 
                                href={item.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-gray-800/40 rounded-xl border border-gray-700 hover:border-brand-orange/50 transition-all group"
                            >
                                <LinkIcon className="h-4 w-4 text-brand-orange" />
                                <span className="text-xs font-bold text-gray-300 truncate uppercase tracking-widest">{item.title || item.url.replace(/https?:\/\/(www\.)?/, '').split('/')[0]}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Endorsements Box */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm font-urbanist overflow-hidden">
                <div 
                    className="flex justify-between items-center mb-6 cursor-pointer group"
                    onClick={() => handleViewEndorserList(user)}
                >
                    <h2 className="text-xl font-bold text-white tracking-tight border-l-4 border-brand-orange pl-4 uppercase flex items-center gap-2 group-hover:text-brand-orange transition-colors">
                        {t('ENDORSED_BY')} ({endorsementsCount})
                        <span className="text-[10px] bg-gray-800 px-2 py-1 rounded text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity tracking-widest uppercase">Explore</span>
                    </h2>
                </div>
                <div className="space-y-4">
                    {endorsementsList.slice(0, 5).map(endorser => (
                        <div key={endorser.handle} onClick={() => handleViewProfile(endorser)} className="flex items-center space-x-3 cursor-pointer group">
                            <img src={endorser.avatarUrl} className="h-10 w-10 rounded-full border border-gray-700 group-hover:border-brand-orange transition-colors" alt="" />
                            <div className="flex-1 min-w-0">
                                <p className="font-bold text-white group-hover:text-brand-orange transition-colors truncate text-sm uppercase">{endorser.name}</p>
                                <p className="text-[10px] text-brand-muted font-mono">@{endorser.handle}</p>
                            </div>
                        </div>
                    ))}
                    {endorsementsCount === 0 && <p className="text-brand-muted text-sm italic">No endorsements yet.</p>}
                </div>
            </div>

            {/* Fan Box */}
            <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm font-urbanist overflow-hidden">
                <div 
                    className="flex justify-between items-center mb-6 cursor-pointer group"
                    onClick={() => handleViewFanList(user)}
                >
                    <h2 className="text-xl font-bold text-white tracking-tight border-l-4 border-pink-500 pl-4 group-hover:text-pink-500 transition-colors flex items-center gap-2 uppercase">
                        {t('FANS')} ({fansCount})
                        <span className="text-[10px] bg-gray-800 px-2 py-1 rounded text-brand-muted opacity-0 group-hover:opacity-100 transition-opacity tracking-widest uppercase">Explore</span>
                    </h2>
                </div>
                <div className="grid grid-cols-4 gap-3">
                    {(user.fans || []).slice(0, 8).map(fanHandle => (
                        <div 
                            key={fanHandle} 
                            className={`aspect-square bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-colors ${isOwnProfile ? 'hover:border-pink-500/50 cursor-pointer' : 'cursor-default'}`}
                            onClick={(e) => {
                                if (!isOwnProfile) return;
                                const fanUser = allUsers.find(u => u.handle === fanHandle);
                                if (fanUser) handleViewProfile(fanUser);
                            }}
                        >
                            <img src={`https://i.pravatar.cc/150?u=${fanHandle}`} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all" alt="" />
                        </div>
                    ))}
                    {fansCount === 0 && <p className="col-span-full text-brand-muted text-sm italic">No fans yet.</p>}
                </div>
                {fansCount > 8 && (
                    <button 
                        onClick={() => handleViewFanList(user)}
                        className="w-full mt-4 text-[10px] font-black text-pink-500 hover:underline uppercase tracking-widest text-center"
                    >
                        View All Fans
                    </button>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

const ProjectsCard: React.FC<{projects: Project[], onProjectClick: (p: Project) => void}> = ({projects, onProjectClick}) => {
    const { t } = useLanguage();
    return (
        <div className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm font-urbanist">
            <h2 className="text-xl font-bold text-white mb-4 tracking-tight border-l-4 border-brand-orange pl-4 uppercase">{t('PROJECTS_AFFILIATIONS')}</h2>
            <ul className="space-y-3">
                {projects.map(p => (
                    <li key={p.id} onClick={() => onProjectClick(p)} className="flex items-center space-x-4 bg-gray-800/40 p-3 rounded-xl border border-gray-800 transition-all hover:bg-gray-800/60 hover:border-brand-orange cursor-pointer group">
                        <div className="h-10 w-10 bg-brand-orange/10 rounded-lg flex items-center justify-center border border-brand-orange/20 group-hover:bg-brand-orange/20 transition-colors"><BriefcaseIcon className="h-5 w-5 text-brand-orange"/></div>
                        <div><p className="font-bold text-white text-sm tracking-tight group-hover:text-brand-orange transition-colors uppercase">{p.name}</p><p className="text-xs text-brand-muted font-mono">{p.city}</p></div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ProfilePage;
